import React, { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { useUser } from "@/shared/lib/hooks/useUser/useUser";
import { useAdminUI } from "@/shared/config/admin";
import { ACHIEVEMENTS_PUBLIC } from "@/features/achievements/model/flags";

/*
 * /:lang/account/achievements — «Достижения» (своя страница, PLAN §5 F3,
 * ADDENDUM 30.09 §4–§5), пока предпросмотр для администраторов.
 *
 * Тот же приём, что у LeagueUsersRoute: до страницы этот адрес ловил общий
 * маршрут «*» (NOT_FOUND) и отвечал голым «404» всем. Так и остаётся для всех,
 * кроме тех, кому открыт предпросмотр, — маршрут плоский и решает сам:
 *   не вошёл                                        → «404», ни одного запроса;
 *   вошёл, не администратор или «как обычный»       → «404»;
 *   администратор (или ACHIEVEMENTS_PUBLIC)         → каркас кабинета + страница.
 * Пока грузится список администраторов — пусто (решать не из чего).
 * Сервер (/api/achievements/app/me) отвечает 404 всем остальным — второй замок.
 */

const AccountPage = lazy(() => import("@/pages/Account").then((m) => ({ default: m.default })));
const AchievementsPage = lazy(() =>
    import("@/pages/AchievementsPage").then((m) => ({ default: m.AchievementsPage })),
);

/** Ровно то, что рисует NOT_FOUND в routeConfig. */
const NotFound = () => <div>404</div>;

export const AchievementsRoute = () => {
    const { user } = useUser();
    const email = user && typeof user !== "string" ? user.email || "" : "";
    if (!email) return <NotFound />;
    return <PreviewOnly email={email} />;
};

const PreviewOnly = ({ email }: { email: string }) => {
    const { isAdmin, loading } = useAdminUI(email);
    const open = ACHIEVEMENTS_PUBLIC || isAdmin;
    if (!open && loading) return <div data-testid="achievements-checking" />;
    if (!open) return <NotFound />;
    return (
        <Routes>
            <Route
                element={
                    <Suspense fallback="">
                        <AccountPage />
                    </Suspense>
                }
            >
                <Route
                    path="*"
                    element={
                        <Suspense fallback="">
                            <AchievementsPage />
                        </Suspense>
                    }
                />
            </Route>
        </Routes>
    );
};

export default AchievementsRoute;
